import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BG1 from "./../../../assets/img/giftshop/landing/bg-1.png";
import RightArrow from "../../../compoment/icon/RightArrow";
import {
  CaricatureHobbyBackgroundImage,
  backgroundImageUrl,
  compositeCharactersOnBackground,
  getActiveCaricatureHobbyBackgroundImages,
  isTransparentHobbyBackgroundName,
  persistFavouriteTransparencyFlag,
} from "../../../services/caricatureHobbyService";
import {
  finalizeOrderWithGeneratedArt,
  persistLatestChosenArtUrl,
} from "../../../utils/finalizeOrderWithGeneratedArt";
import Loader from "../../../compoment/ui/Loader";
import type { RootState } from "../../../redux/store";
import {
  appendHobbyPersonCompletedResult,
  setProcessedImageUrl,
} from "../../../redux/slices/uploadFlowSlice";
import { navigateToNextPersonCapture } from "../../../utils/hobbyPersonFlow";
import {
  appendHobbyCharacterFavouriteUrl,
  persistHobbyCharacterFavouriteUrls,
  persistHobbyCharacterNologoPairs,
  persistHobbyGenerationResult,
} from "../../../utils/hobbyGenerationNavigation";
import { resolveHobbyCaricatureRouteSlug } from "../../../services/stylePromptResolver";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
import SignOutConfirmModal from "../../../modal/SignOutConfirmModal";

const SELECTED_BG_STORAGE_KEY = "selectedCaricatureHobbyBackground";
const BACKGROUND_SHIMMER_COUNT = 6;

const HobbyBackgroundImageTile = ({
  src,
  alt,
  selected,
  onSelect,
  title,
  overlayLabel,
  /** Checkerboard under the sample so true/alpha transparency is visible. */
  showTransparencyCheckerboard = false,
}: {
  src: string;
  alt: string;
  selected: boolean;
  onSelect: () => void;
  title?: string;
  /** Optional text drawn on top of the background image. */
  overlayLabel?: string;
  showTransparencyCheckerboard?: boolean;
}) => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const hasSrc = Boolean(src && src.trim());

  useEffect(() => {
    setLoaded(false);
    if (!hasSrc) {
      setLoaded(true);
      return;
    }
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src, hasSrc]);

  return (
    <div
      className={`bgItem hobbyBackgroundSlide ${selected ? "selected" : ""}${
        showTransparencyCheckerboard ? " hobbyBackgroundSlide--transparent" : ""
      }`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      title={title}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <span
        className={`hobbyBackgroundImgWrap${loaded ? " is-loaded" : ""}${
          showTransparencyCheckerboard ? " hobbyBackgroundImgWrap--checkerboard" : ""
        }`}
      >
        {!loaded && <span className="hobbyBackgroundShimmer" aria-hidden="true" />}
        {hasSrc ? (
          <img
            ref={imgRef}
            src={src}
            alt={alt}
            onLoad={() => setLoaded(true)}
            onError={(e) => {
              const el = e.currentTarget;
              // For transparent tiles, hide a broken/opaque fallback so checkerboard remains.
              if (showTransparencyCheckerboard) {
                el.style.display = "none";
                setLoaded(true);
                return;
              }
              if (!el.dataset.fallbackApplied) {
                el.dataset.fallbackApplied = "1";
                el.src = BG1;
              }
              setLoaded(true);
            }}
          />
        ) : null}
        {overlayLabel ? (
          <span className="hobbyBackgroundOverlayLabel">{overlayLabel}</span>
        ) : null}
      </span>
    </div>
  );
};

const CreatedPersonHobby = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const dispatch = useDispatch();
  const { hobbyPeopleCount, hobbyPersonCompletedResults } = useSelector(
    (s: RootState) => s.uploadFlow
  );
  const [backgrounds, setBackgrounds] = useState<CaricatureHobbyBackgroundImage[]>([]);
  const [backgroundsLoading, setBackgroundsLoading] = useState(true);
  const [backgroundsError, setBackgroundsError] = useState<string | null>(null);
  const [generatedImageUrls, setGeneratedImageUrls] = useState<string[]>([]);
  const [selectedGeneratedIndex, setSelectedGeneratedIndex] = useState(0);
  /** `null` = no background */
  const [selectedBackgroundId, setSelectedBackgroundId] = useState<string | null>(null);
  const [isCompositing, setIsCompositing] = useState(false);
  const [showBackConfirmModal, setShowBackConfirmModal] = useState(false);
  const [showProceedConfirmModal, setShowProceedConfirmModal] = useState(false);
  const backgroundSliderRef = useRef<HTMLDivElement>(null);

  const stationData = useMemo(() => {
    const fromNav = (location.state as { stationData?: Record<string, unknown> } | null)?.stationData;
    if (fromNav && typeof fromNav === "object") {
      return fromNav;
    }
    const selectedRaw = localStorage.getItem("selectedStation");
    const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
    const fromStorage = JSON.parse(localStorage.getItem("stationData") || "{}");
    return fromSelected || fromStorage;
  }, [location.state]);

  // Prefer URL slug so parent category (caricature-cartoon) does not replace hobby-caricature.
  const routeSlug = useMemo(
    () => resolveHobbyCaricatureRouteSlug(slug),
    [slug]
  );

  const isFinalCombine = Boolean(
    (location.state as { isFinalCombine?: boolean } | null)?.isFinalCombine
  );
  const currentPersonIndex = useMemo(() => {
    const fromState = (location.state as { currentPersonIndex?: number } | null)?.currentPersonIndex;
    if (typeof fromState === "number" && fromState >= 0) {
      return fromState;
    }
    return Math.max(0, hobbyPersonCompletedResults.length);
  }, [location.state, hobbyPersonCompletedResults.length]);

  const totalPeople = hobbyPeopleCount ?? 1;
  const isMultiPerson = totalPeople > 1;
  const showBackgroundStep = !isMultiPerson || isFinalCombine;
  const currentPersonNumber = currentPersonIndex + 1;

  useEffect(() => {
    const fromStateResult = (location.state as any)?.generationResult;
    const fromState = fromStateResult?.imageUrls;
    if (Array.isArray(fromState) && fromState.length > 0) {
      const urls = fromState.filter((url: unknown) => typeof url === "string");
      setGeneratedImageUrls(urls);
      persistHobbyCharacterNologoPairs(urls, fromStateResult?.imageUrlsNoLogo);
      if (isFinalCombine || !isMultiPerson) {
        persistHobbyCharacterFavouriteUrls(urls);
      }
      return;
    }
    try {
      const fromStorage = JSON.parse(localStorage.getItem("hobbyCaricatureGenerationResult") || "{}");
      const fromStorageUrls = Array.isArray(fromStorage?.imageUrls) ? fromStorage.imageUrls : [];
      const urls = fromStorageUrls.filter((url: any) => typeof url === "string");
      setGeneratedImageUrls(urls);
      persistHobbyCharacterNologoPairs(urls, fromStorage?.imageUrlsNoLogo);
      if (urls.length > 0 && (isFinalCombine || !isMultiPerson)) {
        persistHobbyCharacterFavouriteUrls(urls);
      }
    } catch {
      setGeneratedImageUrls([]);
    }
  }, [location.state, isFinalCombine, isMultiPerson]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBackgroundsLoading(true);
      setBackgroundsError(null);
      const res = await getActiveCaricatureHobbyBackgroundImages();
      if (cancelled) return;
      if (!res.success && res.data.length === 0) {
        setBackgroundsError(res.error || "Could not load backgrounds");
        setBackgrounds([]);
      } else {
        setBackgrounds(res.data);
        // Default select display index 0: first transparent / No Background tile.
        const firstTransparent = res.data.find((bgItem) =>
          isTransparentHobbyBackgroundName(bgItem.name)
        );
        setSelectedBackgroundId(firstTransparent?.id ?? res.data[0]?.id ?? null);
      }
      setBackgroundsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const proceed = async () => {
    if (isCompositing) return;
    const selectedGenerated = generatedImageUrls[selectedGeneratedIndex] || generatedImageUrls[0] || null;

    if (isMultiPerson && !isFinalCombine) {
      if (!selectedGenerated) return;
      appendHobbyCharacterFavouriteUrl(selectedGenerated);
      dispatch(appendHobbyPersonCompletedResult(selectedGenerated));
      const completedCount = hobbyPersonCompletedResults.length + 1;

      if (completedCount < totalPeople) {
        navigateToNextPersonCapture(navigate, routeSlug);
        return;
      }

      const allCharacterUrls = [...hobbyPersonCompletedResults, selectedGenerated];
      persistHobbyCharacterFavouriteUrls(allCharacterUrls);
      const combinedResult = { imageUrls: allCharacterUrls };
      persistHobbyGenerationResult(combinedResult);
      navigate(`/${routeSlug}/created-person-hobby`, {
        replace: true,
        state: {
          generationResult: combinedResult,
          stationData,
          isFinalCombine: true,
        },
      });
      return;
    }

    let finalSelectedImage = selectedGenerated;

    const characterUrls = generatedImageUrls.filter((url) => typeof url === "string" && url.trim().length > 0);
    const bg =
      selectedBackgroundId !== null
        ? backgrounds.find((b) => b.id === selectedBackgroundId)
        : undefined;
    const choseTransparent = isTransparentHobbyBackgroundName(bg?.name);

    if (characterUrls.length > 0) {
      persistHobbyCharacterFavouriteUrls(characterUrls);
    }

    if (characterUrls.length > 0) {
      const bgUrl = bg ? backgroundImageUrl(bg) : "";

      setIsCompositing(true);
      setBackgroundsError(null);
      const compositeRes = await compositeCharactersOnBackground({
        characterUrls,
        backgroundUrl: bgUrl,
        backgroundName: bg?.name,
      });
      setIsCompositing(false);
      if (!compositeRes.success || !compositeRes.imageUrl) {
        setBackgroundsError(compositeRes.error || "Failed to merge caricatures.");
        return;
      }
      finalSelectedImage = compositeRes.imageUrl;
    }

    persistFavouriteTransparencyFlag(choseTransparent);

    if (finalSelectedImage) {
      try {
        localStorage.setItem("favouriteImg", finalSelectedImage);
      } catch {
        /* ignore */
      }
      persistLatestChosenArtUrl(finalSelectedImage);
      dispatch(setProcessedImageUrl(finalSelectedImage));

      const orderResult = await finalizeOrderWithGeneratedArt(finalSelectedImage);
      if (!orderResult.success && !orderResult.skipped) {
        setBackgroundsError(orderResult.error || "Failed to attach your artwork to the order.");
        return;
      }
    }
    if (selectedBackgroundId === null) {
      localStorage.removeItem(SELECTED_BG_STORAGE_KEY);
    } else if (bg) {
      try {
        localStorage.setItem(
          SELECTED_BG_STORAGE_KEY,
          JSON.stringify({
            id: bg.id,
            name: bg.name,
            image_url: backgroundImageUrl(bg),
          })
        );
      } catch {
        /* ignore */
      }
    }
    // Skip save-favorite → go straight to merch after successful create.
    navigate(`/${routeSlug}/print-frame-selection`, {
      state: {
        stationData,
        snapshot: finalSelectedImage || undefined,
        selectedHobbyBackground: bg ?? null,
        selectedHobbyGeneratedImage: finalSelectedImage,
        hobbyCharacterFavouriteUrls: characterUrls,
        isTransparentHobbyBackground: choseTransparent,
      },
    });
  };

  const scrollBackgroundSlider = (direction: "prev" | "next") => {
    const el = backgroundSliderRef.current;
    if (!el) return;
    const amount = Math.min(260, el.clientHeight * 0.75);
    el.scrollBy({ top: direction === "next" ? amount : -amount, behavior: "smooth" });
  };

  const headingText = isFinalCombine
    ? "All Caricature Hobbies Successfully Created"
    : isMultiPerson
      ? `Person ${currentPersonNumber} of ${totalPeople} — Caricature Ready`
      : "Caricature Hobby Successfully Created";

  const descriptionText = isFinalCombine
    ? "Everyone’s caricatures are ready. Choose a background to combine them, then proceed."
    : isMultiPerson
      ? "This person’s caricature looks great. Proceed to continue with the next person."
      : "A unique caricature has been generated based on the selected photo. Feel free to proceed when ready.";

  const proceedLabel = isMultiPerson && !isFinalCombine ? "Continue to next person" : "Proceed";

  const handleProceedClick = () => {
    if (isCompositing) return;
    if (showBackgroundStep) {
      setShowProceedConfirmModal(true);
      return;
    }
    void proceed();
  };

  const handleProceedConfirm = () => {
    setShowProceedConfirmModal(false);
    void proceed();
  };

  return (
    <>
      {isCompositing && <Loader />}
      <StationThemeLayout
        className="personHobbyCreatedSection"
        station={stationData as { backgroundImageUrl?: string; type?: string; name?: string }}
        showStationLogo
        onBack={() => setShowBackConfirmModal(true)}
      >
        <div className="personHobbyCreatedSplit">
          <div>
            {/* <div className="personHobbyCreatedScroll mb-5 mobilefFirstSection pb-4">
              <div className="selectBrand">
                <div className="heading">{headingText}</div>
                <div className="description px-0">
                  {descriptionText}
                </div>
              </div>
            </div> */}
            <aside className="personHobbyCreatedPreview shadow-sm" aria-label="Caricature preview">
              <div className="personHobbyCreatedPreviewInner">
                <img
                  src={generatedImageUrls[selectedGeneratedIndex] || generatedImageUrls[0] || ""}
                  alt="Generated hobby caricature"
                  className="hobbyImg"
                />
                {generatedImageUrls.length > 1 && (
                  <div className="personHobbyVariantStrip">
                    {generatedImageUrls.map((img, idx) => (
                      <button
                        type="button"
                        key={`${img}-${idx}`}
                        className={`personHobbyVariantThumb ${selectedGeneratedIndex === idx ? "is-selected" : ""}`}
                        onClick={() => setSelectedGeneratedIndex(idx)}
                        aria-label={`Use generated image ${idx + 1}`}
                      >
                        <img src={img} alt="" />
                      </button>
                    ))}
                  </div>
                )}
                {!generatedImageUrls.length && (
                  <div className="small text-warning mt-2 text-center px-2">
                    No generated image was found from the API response. You can still continue.
                  </div>
                )}
              </div>
            </aside>

            <div className="personHobbyCreatedScroll">
              <div className="selectBrand">
                <div className="desktopfFirstSection">
                  <div className="heading">{headingText}</div>
                  <div className="description">
                    {descriptionText}
                  </div>
                </div>

                <div className="personHobbyCreatedControls">
                  {showBackgroundStep ? (
                    <>
                      <div className="changeBGTxt">Choose Background</div>
                      <div className="hobbyBackgroundSliderRow mb-3">
                        <div className="hobbyBackgroundSlider row g-3" ref={backgroundSliderRef}>
                          {backgroundsLoading ? (
                            Array.from({ length: BACKGROUND_SHIMMER_COUNT }).map((_, index) => (
                              <div key={`bg-shimmer-${index}`} className="col-6">
                                <div className="bgItem hobbyBackgroundSlide hobbyBackgroundSlideShimmer" aria-hidden="true">
                                  <span className="hobbyBackgroundShimmer" />
                                </div>
                              </div>
                            ))
                          ) : (
                            <>
                              {(() => {
                                const transparentBackgrounds = backgrounds.filter((bgItem) =>
                                  isTransparentHobbyBackgroundName(bgItem.name)
                                );
                                const imageBackgrounds = backgrounds.filter(
                                  (bgItem) => !isTransparentHobbyBackgroundName(bgItem.name)
                                );

                                const renderBackgroundTile = (
                                  bgItem: CaricatureHobbyBackgroundImage,
                                  isTransparentBackground: boolean
                                ) => {
                                  // Transparent tile uses checkerboard only — admin sample images
                                  // are often opaque white and hide true transparency.
                                  const src = isTransparentBackground
                                    ? ""
                                    : backgroundImageUrl(bgItem) || BG1;

                                  return (
                                    <div key={bgItem.id} className="col-6">
                                      <HobbyBackgroundImageTile
                                        src={src}
                                        alt={
                                          isTransparentBackground
                                            ? "No Background"
                                            : "Background"
                                        }
                                        selected={selectedBackgroundId === bgItem.id}
                                        onSelect={() => setSelectedBackgroundId(bgItem.id)}
                                        showTransparencyCheckerboard={isTransparentBackground}
                                        overlayLabel={
                                          isTransparentBackground
                                            ? "No Background"
                                            : undefined
                                        }
                                      />
                                    </div>
                                  );
                                };

                                return (
                                  <>
                                    {transparentBackgrounds.map((bgItem) =>
                                      renderBackgroundTile(bgItem, true)
                                    )}
                                    {/* Always show Plain White right after transparent / No Background. */}
                                    <div className="col-6">
                                      <div
                                        className={`bgItem hobbyBackgroundSlide ${selectedBackgroundId === null ? "selected" : ""}`}
                                        onClick={() => setSelectedBackgroundId(null)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            setSelectedBackgroundId(null);
                                          }
                                        }}
                                      >
                                        <div className="noBGSection shadow-sm">Plain White Background</div>
                                      </div>
                                    </div>
                                    {imageBackgrounds.map((bgItem) =>
                                      renderBackgroundTile(bgItem, false)
                                    )}
                                  </>
                                );
                              })()}
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  ) : null}

                  <button className="proceedBTN mt-4" onClick={handleProceedClick} disabled={isCompositing}>
                    {proceedLabel}
                    <span>
                      {" "}
                      <RightArrow />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </StationThemeLayout>
      {showBackConfirmModal && (
        <SignOutConfirmModal
          show={showBackConfirmModal}
          onHide={() => setShowBackConfirmModal(false)}
          onConfirm={() => {
            setShowBackConfirmModal(false);
            navigate("/");
          }}
          message="Are you sure you want to go back to the home screen?"
          cancelLabel="No"
          confirmLabel="Yes"
        />
      )}
      {showProceedConfirmModal && (
        <SignOutConfirmModal
          show={showProceedConfirmModal}
          onHide={() => setShowProceedConfirmModal(false)}
          onConfirm={handleProceedConfirm}
          message="Are you sure you want to proceed with this background?"
          cancelLabel="No"
          confirmLabel="Yes"
        />
      )}
    </>
  );
};

export default CreatedPersonHobby;
